
import { motion } from 'framer-motion';
import { ExternalLink, RotateCcw, Eye, CheckCircle, XCircle, Clock } from 'lucide-react';

interface PluginCardData {
  title: string;
  description: string;
  status: 'completed' | 'failed' | 'running' | 'pending';
  data?: { url?: string; [key: string]: unknown };
  actions?: string[];
}

interface PluginCardProps {
  data: PluginCardData;
  onAction?: (action: string) => void;
}

const PluginCard = ({ data, onAction }: PluginCardProps) => {
  const getStatusIcon = () => {
    switch (data.status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'running':
        return <Clock className="w-4 h-4 text-blue-400 animate-pulse" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      default:
        return null;
    }
  };



  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'view pr':
      case 'view app':
      case 'view details':
        return <ExternalLink className="w-3 h-3" />;
      case 'retry':
        return <RotateCcw className="w-3 h-3" />;
      case 'view logs':
      case 'view results':
        return <Eye className="w-3 h-3" />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      className="w-[384px] min-w-[384px] max-w-[384px] arc-glass-panel shadow-md hover:shadow-lg transition-all duration-150 ease-in-out p-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 min-w-0 flex-1 mr-2">
          {getStatusIcon()}
          <h4 className="text-base font-medium text-gray-100 truncate">{data.title}</h4>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full border min-w-[4rem] text-center ${
          data.status === 'completed' ? 'border-emerald-500/30 bg-emerald-600/20 text-emerald-300' :
          data.status === 'failed' ? 'border-red-500/30 bg-red-600/20 text-red-300' :
          data.status === 'running' ? 'border-blue-500/30 bg-blue-600/20 text-blue-300' :
          'border-yellow-500/30 bg-yellow-600/20 text-yellow-300'
        }`}>
          {data.status}
        </span>
      </div>

      <p className="text-sm text-slate-400 mt-1 line-clamp-2">
        {data.description}
      </p>

      {data.data?.url && (
        <div className="mb-3 p-2 bg-slate-800/30 rounded text-sm text-slate-400 overflow-hidden">
          <span className="font-mono break-all text-ellipsis overflow-hidden whitespace-nowrap block">{data.data.url}</span>
        </div>
      )}

      {data.actions && data.actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.actions.map((action, index) => (
            <motion.button
              key={index}
              onClick={() => onAction?.(action)}
              className="px-2 py-1 text-sm bg-slate-700/50 text-gray-100 rounded hover:bg-slate-600/50 hover:text-white flex items-center space-x-1 border border-slate-600/30 max-w-full transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {getActionIcon(action)}
              <span className="truncate">{action}</span>
            </motion.button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default PluginCard;
export { PluginCard };
